import { Customer } from 'Services/Mongo/Types';
import {
  Product,
  ProductColumn,
  ProductColumnsIds
} from '@setvi/shared/interfaces';
import { EditorProduct } from '@setvi/shared/components/seditor/interfaces';
import { getProductCardAsHtml } from '@setvi/shared/components/seditor/hooks/use-editor-products/heplers';

export { SINGER_FACET_LABEL_OVERRIDES } from '@setvi/shared/utils/singer-labels';

const removeWords = ['Custom'];

export const getProductStock = (inventory: Product['inventory']): number =>
  inventory?.reduce((acc, curr) => acc + (curr?.availableOnHand || 0), 0) || 0;

export const getProductStockByCustomer = (
  inventory: Product['inventory'],
  customer: Customer
) =>
  inventory?.find(
    // eslint-disable-next-line no-underscore-dangle
    warehouse => warehouse.warehouseId === customer?.warehouse?.code
  )?.availableOnHand || 0;

/**
 * Stock to show for a product, scoped to the customer when — and only when —
 * that customer actually resolves to a warehouse.
 *
 * `warehouse.code` is the only field inventory rows are matched on, and it lives
 * on the *full* customer record. A customer object without it (a prospect, an
 * account with no warehouse assigned, or a quote's slim `account` projection
 * before `useQuoteCustomer` / `useQuote` has merged the full body in) matches no
 * inventory row at all, so scoping would report every product out of stock
 * (QOT-1608). Falling back to the all-warehouse total keeps the figure truthful
 * instead of wrong.
 */
export const getStockForCustomer = (
  inventory: Product['inventory'],
  customer?: Customer
) =>
  customer?.warehouse?.code
    ? getProductStockByCustomer(inventory, customer)
    : getProductStock(inventory);

export const parseProductsToEmailProduct = (
  products: Product[],
  columns?: ProductColumn[]
): EditorProduct[] => {
  const ids = columns ? new Set(columns.map(c => c.id)) : null;
  const hasPrice =
    !ids ||
    ids.has(ProductColumnsIds.Price) ||
    ids.has(ProductColumnsIds.ListPrice) ||
    ids.has(ProductColumnsIds.GetPrice);
  const hasManufacturer = !ids || ids.has(ProductColumnsIds.Manufacturer);

  return products.map(product => ({
    productName: product.name,
    sku: product.sku,
    ...(hasPrice ? { price: product.price } : {}),
    // @ts-ignore
    productLink: product.custom.ecommerceLink?.value,
    imageUrl: product.imageUrl || product.images[0]?.path,
    ...(hasManufacturer
      ? {
          vendorName: product?.vendor?.name,
          manufacturerSku: product?.manufacturerSku
        }
      : {})
  }));
};

/**
 * Render selected products as the email product-card HTML. Shared by the mobile catalog
 * and the Outlook catalog dialog so both insert identical, consistent cards.
 */
export const getSelectedProductsHTML = (products: Product[]): string =>
  parseProductsToEmailProduct(products)
    .map(product => getProductCardAsHtml(product, false))
    .join('');

export const getInStock = (
  inventory: Product['inventory'],
  customer: Customer
) => !!getStockForCustomer(inventory, customer);

export const LIMIT_SELECTED_PRODUCTS = 50;
export const getNumOfProductsInComposeMail = (values: any) => {
  const parser = new DOMParser();

  const parsedHTML = parser.parseFromString(values.body, 'text/html');

  const selectedProductsList =
    parsedHTML.getElementsByClassName('selected-product');

  return selectedProductsList.length;
};

export const transformAlgoliaFacetLabel = (label: string) => {
  // Splits label on capital letters, dots and underscores
  const words = label.split(/(?=[A-Z])|\.|_/);

  const filteredWords = words.filter(
    i => !removeWords.some(j => j.toLocaleLowerCase() === i.toLocaleLowerCase())
  );

  // Capitalizes first letter of each word and lowercases the rest and adds a space between words
  const transformedLabel = filteredWords
    .map(
      word =>
        (word[0] || '').toUpperCase() + (word.slice(1) || '').toLowerCase()
    )
    .join(' ')
    .trim();
  return transformedLabel;
};

export enum ProductActionType {
  DetailsView = 'ProductDetails',
  AssetView = 'View Asset',
  AddProductToEmail = 'Add Products to Email',
  Search = 'Search',
  CustomerSelection = 'Customer Selection',
  CategorySelection = 'Category Selection',
  FiltersSelection = 'Filters Selection'
}

export const valueToMonetary = (
  value: number,
  options?: { [key: string]: string }
) =>
  value > 0 && Number.isFinite(value)
    ? `${value.toLocaleString('en-US', {
        currency: 'USD',
        style: 'currency',
        ...options
      })}`
    : '-';

export const parseKeyValueArrayToObject = (inputArray: string[]) => {
  const result: Record<string, string | string[]> = {};

  inputArray.forEach(entry => {
    const [key, ...rest] = entry.split(':');
    const rawValue = rest.join(':');

    const parsedValue: string = rawValue;

    if (Object.prototype.hasOwnProperty.call(result, key)) {
      if (Array.isArray(result[key])) {
        (result[key] as string[]).push(parsedValue);
      } else {
        result[key] = [result[key] as string, parsedValue];
      }
    } else {
      result[key] = parsedValue;
    }
  });

  return result;
};

export const appendPricingToProducts = (products: Product[], prices: any[]) =>
  products.map(item => {
    const pricing = prices?.find(i => i.sku === item.sku);
    const obtainedPrice = pricing?.price;
    const obtainedCost = pricing?.cost;
    // @ts-ignore Incorrect response type on getPrices API
    const obtainedIsEditable = pricing?.isEditable;

    return {
      ...item,
      ...(typeof obtainedPrice === 'number' ? { price: obtainedPrice } : {}),
      ...(typeof obtainedCost === 'number'
        ? { customerCost: obtainedCost }
        : {}),
      ...(typeof obtainedIsEditable === 'boolean'
        ? { isEditable: obtainedIsEditable }
        : {}),
      // Pricing ran and came back with nothing usable for this SKU — the cell
      // shows the failure instead of a bare "-" (DEV-495).
      ...(pricing ? { pricingFailed: !!pricing.pricingFailed } : {})
    };
  });
